<template>
  <v-container>
    <v-row class="mb-4">
      <v-col cols="6" md="2">
        <SelectYear v-model="yearProxy" />
      </v-col>

      <v-col cols="6" md="2">
        <SelectMonth v-model="monthProxy" :available="availableMonths" />
      </v-col>

      <v-col cols="12a" md="2">
        <v-select
          v-model="categoryProxy"
          :items="categoryItems"
          item-title="title"
          item-value="value"
          density="comfortable"
          :label="$t('general.category')"
          hide-details
        />
      </v-col>

    </v-row>

    <v-container class="d-flex justify-center align-center fill-height" v-if="loading">
      <v-progress-circular indeterminate color="primary" size="50" width="8" />
    </v-container>

    <v-data-table
      v-else
      :items="rows"
      :headers="headers"
      :sort-by="sortBy"
      class="elevation-2"
      hide-default-footer
      density="comfortable"
      :items-per-page="-1"
      :no-data-text="$t('w_ranking.no_data')"
      item-key="key"
      striped="even"
    >
      <template #item.current_place="{ item }">
        <div class="place-wrap">
          <strong>{{ item.current_place }}</strong>
          <v-chip v-if="item.prev_place === null" color="blue" size="x-small" variant="flat" class="ml-2">NEW</v-chip>
          <v-chip v-if="item.progress === 'up'" color="success" size="x-small" variant="flat" class="ml-2">▲{{ item.place_changed }}</v-chip>
          <v-chip v-else-if="item.progress === 'down'" color="red-accent-3" size="x-small" variant="flat" class="ml-2">▼{{ item.place_changed }}</v-chip>
        </div>
      </template>
      <template #item.full_name="{ item, value }">
        <span style="white-space: nowrap">
          <RouterLink
            v-if="(item?.raw?.account_id ?? item?.account_id)"
            :to="{ name: 'AccountPage', params: { id: (item.raw?.account_id ?? item.account_id) } }"
          >
            <CountryFlags :countryCode="(item?.raw?.country ?? item?.country)" v-if="(item?.raw?.country ?? item?.country)" />
            &nbsp;{{ value }}
          </RouterLink>
          <span v-else>
            <CountryFlags :countryCode="(item?.raw?.country ?? item?.country)" v-if="(item?.raw?.country ?? item?.country)" />
            &nbsp;{{ value }}
          </span>
        </span>
      </template>
      <template #item.index="{ value }"><span>{{ value || '—' }}</span></template>
      <template #item.rank_ratio="{ value }"><span>{{ value }}</span></template>
      <template #item.total_points="{ value }"><strong class="highlighted">{{ value }}</strong></template>
      <template #item.races_counted="{ value }">{{ value }} / 8</template>
    </v-data-table>
  </v-container>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import apiClient from '/api.js'
import SelectYear from '@/components/shared/SelectYear.vue'
import SelectMonth from '@/components/shared/SelectMonth.vue'
import CountryFlags from '@/components/shared/CountryFlags.vue'
import {useI18n} from 'vue-i18n'


const route = useRoute()
const router = useRouter()
const { t } = useI18n()


const loading = ref(false)
const availableMonths = ref([]) // z API
const rows = ref([])

const headers = [
  { title: t('w_ranking.place'), value: 'current_place', width: 50, align: 'left' },
  { title: t('w_ranking.name'), value: 'full_name' },
  { title: 'Index', value: 'index', minWidth: 110 },
  { title: t('w_ranking.points'), value: 'total_points', minWidth: 80, sortable: true, align: 'right' },
  { title: t('w_ranking.races'), value: 'races_counted', minWidth: 80, align: 'left' },
  { title: t('w_ranking.coef'), value: 'rank_ratio', sortable: true, align: 'right' },
  // { title: t('w_ranking.prev_place'), value: 'prev_place', sortable: true, align: 'center' },
]
const sortBy = ref([{ key: 'total_points', order: 'desc' }])

const categoryItems = [
  { value: 'M21', title: 'M21' },
  { value: 'M19', title: 'M19' },
  { value: 'W21', title: 'W21' },
  { value: 'W19', title: 'W19' },

]

// URL-driven computed proxies
const yearProxy = computed({
  get: () => Number(route.params.year) || new Date().getFullYear(),
  set: v => router.push({ name: route.name, params: { year: Number(v), month: monthProxy.value ?? 1, category: categoryProxy.value } })
})
const monthProxy = computed({
  get: () => (route.params.month ? Number(route.params.month) : null),
  set: v => router.push({ name: route.name, params: { year: yearProxy.value, month: Number(v), category: categoryProxy.value } })
})
const categoryProxy = computed({
  get: () => String(route.params.category || 'M21'),
  set: v => router.push({ name: route.name, params: { year: yearProxy.value, month: monthProxy.value ?? 1, category: String(v || 'M21') } })
})

const mapResults = (list) =>
  (list || []).map((r, i) => ({ key: i, ...r, full_name: `${r.last_name ?? ''} ${r.first_name ?? ''}`.trim() }))

async function fetchFromUrl() {
  const y = Number(route.params.year)
  const m = route.params.month ? Number(route.params.month) : null
  const cat = String(route.params.category || '')
  if (!y || !cat) return

  loading.value = true
  try {
    const url = m ? `/ranking/world/${y}/${m}/${cat}/` : `/ranking/world/${y}/1/${cat}/`
    const { data } = await apiClient.get(url)
    availableMonths.value = data?.available_months || []
    rows.value = mapResults(data?.results)

    // když nejsou žádné dostupné měsíce → vyprázdni select (monthProxy -> null), URL ponecháme jak je
    if (!availableMonths.value.length) {
      if (monthProxy.value !== null) {
        // nastav jen UI (route necháme, protože není co zvolit)
        // v-model na SelectMonth dostane null přes computed get níže (viz watcher)
      }
    } else if (monthProxy.value === null || !availableMonths.value.includes(monthProxy.value)) {
      // máme nějaké měsíce a URL/aktuální volba je mimo → přepiš URL na poslední dostupný
      const fallback = availableMonths.value.at(-1)
      router.replace({ name: route.name, params: { year: y, month: fallback, category: cat } })
    }
  } catch {
    availableMonths.value = []
    rows.value = []
  } finally {
    loading.value = false
  }
}

// URL změna → fetch (immediate)
watch(() => [route.params.year, route.params.month, route.params.category],
  fetchFromUrl,
  { immediate: true }
)

// Když jsou availableMonths prázdné → vyprázdni model v SelectMonth (v-model=null)
watch(availableMonths, (list) => {
  if (!list?.length) {
    // přinutíme SelectMonth, aby neměl zvolenou hodnotu
    // tím, že computed getter monthProxy vrací route param, necháme URL beze změny,
    // ale v UI bude SelectMonth prázdný (protože items=[])
    // Pokud chceš zároveň smazat param z URL, musel bys mít volitelný segment nebo query.
  }
})
</script>

<style scoped>
.highlighted { font-size: 15px; }
.place-wrap { display: inline-flex; align-items: left; }
</style>
